import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Spinner } from "react-bootstrap";
import { Settings2 } from "lucide-react";
import { techStacksList } from "../../utils/common";
import apiClient from "../../utils/apiClient";
import { AppButton } from "../ui";
import "./AIQuestionForm.scss";

const defaultTechStacks = techStacksList.filter((t) => t !== "");
const difficulties = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

// react-select theme styles aligned with the rest of the app
// (mirrors CodingQuestionBank/TagSelector.js).
const themeStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "var(--input-bg)",
    borderColor: state.isFocused ? "#3b82f6" : "var(--input-border)",
    borderRadius: "8px",
    minHeight: "40px",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59,130,246,0.15)" : "none",
    "&:hover": { borderColor: "var(--text-tertiary)" },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--card-bg)",
    border: "1px solid var(--border-primary)",
    borderRadius: "8px",
    boxShadow: "0 4px 12px var(--card-shadow, rgba(0,0,0,0.15))",
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#3b82f6"
      : state.isFocused
      ? "var(--bg-hover)"
      : "transparent",
    color: state.isSelected ? "#fff" : "var(--text-primary)",
    cursor: "pointer",
  }),
  singleValue: (base) => ({ ...base, color: "var(--text-primary)" }),
  input: (base) => ({ ...base, color: "var(--text-primary)" }),
  placeholder: (base) => ({ ...base, color: "var(--text-tertiary)" }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: "var(--border-primary)" }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "var(--text-tertiary)",
    "&:hover": { color: "var(--text-primary)" },
  }),
};

const toOption = (v) => (v ? { value: v, label: v } : null);

const AIQuestionForm = ({ form, setForm, loading, onSubmit, isEdit = false }) => {
  const [techStacks, setTechStacks] = useState(defaultTechStacks);
  const [stacksLoading, setStacksLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStacksLoading(true);
    apiClient
      .get("/ai/ai-questions/tech-stacks")
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.techStacks;
        if (Array.isArray(list) && list.length > 0) setTechStacks(list);
      })
      .catch(() => {
        /* fall back to defaults */
      })
      .finally(() => {
        if (!cancelled) setStacksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Always include the currently selected techStack, even if it's a legacy
  // value not in the institute's category list.
  const techStackOptions = (() => {
    const set = new Set(techStacks);
    if (form.techStack) set.add(form.techStack);
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((t) => ({ value: t, label: t }));
  })();

  return (
    <form className="ai-form" onSubmit={onSubmit}>
      {/* Question */}
      <div className="ai-form__group">
        <label htmlFor="ai-q-question" className="ai-form__label">
          Question <span className="ai-form__required">*</span>
        </label>
        <textarea
          id="ai-q-question"
          className="ai-form__textarea"
          rows={5}
          value={form.question}
          onChange={(e) => handleField("question", e.target.value)}
          placeholder="Enter the AI interview question..."
          required
        />
        <p className="ai-form__hint">
          Tip: write clear, scoped questions. Avoid multi-part questions in a single entry.
        </p>
      </div>

      {/* Category + Difficulty */}
      <div className="ai-form__row">
        <div className="ai-form__group">
          <div className="ai-form__label-row">
            <label htmlFor="ai-q-category" className="ai-form__label">
              Category <span className="ai-form__required">*</span>
            </label>
            <Link to="/ai-bank/categories" className="ai-form__manage-link">
              <Settings2 size={13} /> Manage categories
            </Link>
          </div>
          <Select
            inputId="ai-q-category"
            classNamePrefix="react-select"
            options={techStackOptions}
            value={toOption(form.techStack)}
            onChange={(opt) => handleField("techStack", opt?.value || "")}
            placeholder={stacksLoading ? "Loading..." : "Search or select a category..."}
            isClearable
            isSearchable
            isLoading={stacksLoading}
            styles={themeStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            noOptionsMessage={() => "No matching category. Add new ones from Manage categories."}
          />
        </div>

        <div className="ai-form__group">
          <label htmlFor="ai-q-difficulty" className="ai-form__label">
            Difficulty
          </label>
          <Select
            inputId="ai-q-difficulty"
            classNamePrefix="react-select"
            options={difficulties}
            value={difficulties.find((d) => d.value === form.difficulty) || difficulties[0]}
            onChange={(opt) => handleField("difficulty", opt?.value || "easy")}
            isSearchable={false}
            styles={themeStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      </div>

      <div className="ai-form__actions">
        <AppButton type="submit" variant="default" disabled={loading || !form.techStack}>
          {loading ? (
            <>
              <Spinner size="sm" animation="border" /> Saving...
            </>
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Create Question"
          )}
        </AppButton>
      </div>
    </form>
  );
};

export default AIQuestionForm;

